import { Component, OnInit, ViewChild, Injectable, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';

import { GenericModalService, ModalSettingsInput, ModalSettingsMessage, ModalSettingsTemplate } from '../shared/generic-modal/generic-modal.service';
import { LoadingPanelService, LoadingPanelSettings } from '../shared/loading-panel/loading-panel.service';

import { User } from './models/user';
import { UserService } from './services/user.service';
import { CurrentUserService } from './services';
import { UserProfileComponent } from './user-profile.component';
import { UserSavedArgs } from './user-profile.component';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';

import { FormsModule } from '@angular/forms';

@Component({
    templateUrl: './user-search-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, FormsModule, UserProfileComponent]
})

export class UserSearchDetailsComponent implements OnInit {

    constructor(
        private userService: UserService,
        private route: ActivatedRoute,
        private router: Router,
        private loadingPanelService: LoadingPanelService,
        private modalService: GenericModalService,
        private changeDetectorRef: ChangeDetectorRef,
        private currentUserService: CurrentUserService) {

         }

    public userIds: number[] = [];
    public users: User[] = [];
    public selectedUser: User = null;
    public isCreatingNewFromElsewhere = false;
    public isCreatingNewFromHere = false;
    public isEditing = false;
    public hideButtonForBuiltInUser = false;
    @ViewChild(UserProfileComponent, { static: true }) private userProfileComponent: UserProfileComponent;

    ngOnInit(): void {
        this.route.params.subscribe((parms: any) => {
            if (parms.userIds) {
                this.userIds = parms.userIds.split(',').map(Number) as number[];
                this.refresh();
            }
            else if (parms.createNew) {
                this.isCreatingNewFromElsewhere = true;
                this.createNew();
            }
        });
    }

    public refresh() {
        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Loading users...'));
        this.userService.getUsers(this.userIds).then(loadedUsers => {
            this.users = loadedUsers;
            if (this.users.length > 0) {
                this.selectedUser = this.users[0];
                if (this.selectedUser.IsBuiltIn && !this.currentUserService.hasPermission('System_User_Admin')) {
                    this.hideButtonForBuiltInUser = true;
                }
            }
            this.loadingPanelService.hideLoadingPanel();
        });
    }

    createNew() {
        this.isCreatingNewFromHere = true;
        this.createNewUser();
    }

    createNewUser() {
        this.selectedUser = new User();
    }

    enableUser() {
        this.loadingPanelService.showLoadingPanelDirect('Enabling the user...');
        this.selectedUser.IsDisabled = false;
        this.userService.saveUser(this.selectedUser).then(() => {
            this.loadingPanelService.hideLoadingPanel();
            this.userProfileComponent.init();
        }).catch(() => {
            this.loadingPanelService.hideLoadingPanel();
        });
    }

    disableUser() {
        this.loadingPanelService.showLoadingPanelDirect('Disabling the user...');

        const isCurrentLoginedUser = this.currentUserService.isCurrentLoginedUser(this.selectedUser.UserId);
        if(isCurrentLoginedUser){
            this.loadingPanelService.hideLoadingPanel();
            this.modalService.confirmDirect('Error disabling user',`Trying to dissable user: ${this.selectedUser.DisplayName}, while logged in as user: ${this.selectedUser.DisplayName}`);
        }
        else{
            this.selectedUser.IsDisabled = true;
            this.userService.saveUser(this.selectedUser).then(() => {
                this.loadingPanelService.hideLoadingPanel();
                this.userProfileComponent.init();
            }).catch(() => {
                this.loadingPanelService.hideLoadingPanel();
            });
        }
    }

    public userSaved(args: UserSavedArgs) {
        if (args.isNew) {
            this.userIds.push(args.savedUser.UserId);
            this.users.push(args.savedUser);
            this.selectedUser = args.savedUser;
            this.isCreatingNewFromElsewhere = this.isCreatingNewFromHere = false;
        }
    }

    delete() {
        this.modalService.confirmDirect('Delete user', `Delete the user ${this.selectedUser.DisplayName}?`).then(() => {
            this.loadingPanelService.showLoadingPanelDirect('Deleting user...');
            this.userService.deleteUser(this.selectedUser.UserId).then(() => {
                this.loadingPanelService.hideLoadingPanel();
                this.router.navigate(['user']);
            });
        }).catch(() => { });
    }

    revokeTokens() {
        this.modalService.confirmDirect('Revoke user tokens', `Revoke all tokens for the user ${this.selectedUser.DisplayName}?`).then(() => {
            this.loadingPanelService.showLoadingPanelDirect('Revoking tokens...');
            this.userService.revokeTokens(this.selectedUser.UserId, 'EdiPortal_Web').then(() => {
                this.loadingPanelService.hideLoadingPanel();
                this.router.navigate(['user']);
            });
        }).catch(() => { });
    }

    public cancelPressed() {
        // This is a little blah - but it's done because create new can be triggered by an url or a button inside this component
        if (this.isCreatingNewFromElsewhere) {
            this.router.navigate(['user']);
        }
        else if (this.isCreatingNewFromHere && this.users.length > 0) {
            this.selectedUser = this.users[0];
        }
        this.isCreatingNewFromElsewhere = false;
        this.isCreatingNewFromHere = false;
    }

    canDeactivate() {
        if (this.isEditing || this.isCreatingNewFromElsewhere || this.isCreatingNewFromHere) {
            return this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?').then(() => true).catch(() => false);
        }
        return true;
    }
}

@Injectable({
    providedIn: 'root'
})
export class UserSearchDetailsComponentCanDeactivate  {
    constructor() { }

    canDeactivate(component: UserSearchDetailsComponent): Observable<boolean> | Promise<boolean> | boolean {

        return component.canDeactivate();
    }
}
